/** The panel an Admin publishes an approval route from — **card C-D, ADR-0067 decision 1A**.
 *
 * It lives on `/administration`, beside the other workspace-shaped settings (the card's recorded
 * assumption), and `Settings.tsx` renders it on both of its branches: the panel decides for itself
 * what the signed-in role sees.
 *
 * **Four properties, each asserted by a test named after what it protects.**
 *
 * - **No field for a stage's position.** The server numbers stages `1..n` in the order drawn, so a
 *   gap or a duplicate is not expressible. The only thing the screen shows is the number a stage
 *   *will* get — as a counter in CSS, not as an input.
 * - **A route with no stages can be sent, and the screen shows the server's reason.** The obvious
 *   thing is to disable the button until there is a stage, and it would hide the refusal this card
 *   exists to surface: F07's shape is `all([])` being `True`, and the guard that stops it belongs to
 *   the service, where a test can fail it. A form-side check would be a second home for the rule
 *   that agrees with the first until the day it does not. (A *half-drawn* stage — a name with no
 *   reviewer — is held back, since that is certain to be refused for a reason the form can name.)
 * - **A role that cannot publish is told who can.** Not a disabled button and not nothing
 *   (`KEEP.md` 17): a disabled control implies it will be enabled.
 * - **A refusal names the stage, never the person.** That is the server's sentence, rendered as
 *   written (design rule 7): this screen builds no message of its own about who is ineligible.
 *
 * **The reviewer picker is a convenience and not the rule.** It offers active Admins and Approvers
 * because those are the only people the server will accept; the server still checks, and answers
 * the same refusal for a person who does not exist as for one who cannot decide.
 */

import { useState } from "react";
import type { FormEvent } from "react";

import { FieldError } from "../components/FieldError";
import { Loaded, WriteFailed } from "../components/Loaded";
import { useFocusRefusal } from "../components/useFocusRefusal";
import { useFormChecks } from "../components/useFormChecks";
import { Panel } from "../components/Panel";
import { Refused } from "../api/refusal";
import { useMe } from "../api/auth";
import {
  ROUTE_KEY_PATTERN,
  ROUTE_KINDS,
  eligibleReviewers,
  mayPublishRoute,
  usePublishRoute,
  usePublishedRoutes,
} from "../api/approvals";
import type { PublishedRoute, RouteKind, RouteRow } from "../api/approvals";
import { useMembers } from "../api/queries";
import type { Member } from "../api/queries";
import { kindLabel } from "./kinds";
import styles from "./Screen.module.css";
import own from "./ApprovalsRoutes.module.css";

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** A stage being drawn. `id` is for React's key only: the position is its place in the list. */
interface Row {
  id: number;
  name: string;
  reviewerId: string;
}

const ROLE_WORD: Record<string, string> = {
  admin: "Admin",
  approver: "Approver",
};

export function ApprovalsRoutePanel() {
  const { data: me } = useMe();
  if (!mayPublishRoute(me?.role)) {
    return (
      <Panel title="Approval routes" sub="who reviews a figure, and in what order" column="1 / 9">
        {/* **Who can, not nothing and not a disabled button.** A control that would predictably 403
            is not shown (design rule 6); the sentence is what takes its place. */}
        <p className={styles.note} data-testid="route-who-can">
          Only an <strong>Admin</strong> can publish an approval route. A route says who reviews a
          figure and in what order, so it is set by someone who administers the workspace rather
          than by the person submitting to it. Ask an Admin of this workspace.
        </p>
      </Panel>
    );
  }
  return <PublishPanel />;
}

function PublishPanel() {
  const members = useMembers(true);
  const routes = usePublishedRoutes(true);
  const publish = usePublishRoute();

  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [appliesTo, setAppliesTo] = useState<RouteKind>("activity_record");
  const [separation, setSeparation] = useState(true);
  const [rows, setRows] = useState<Row[]>([]);
  const [nextId, setNextId] = useState(1);
  const [published, setPublished] = useState<PublishedRoute | null>(null);

  const keyOk = ROUTE_KEY_PATTERN.test(key.trim());
  const [checks, attach] = useFormChecks();
  // After the server refuses (a reviewer who cannot decide, a route with no stages), focus goes to
  // its sentence rather than staying on the button (G-P8-45).
  useFocusRefusal(checks.element, publish.error);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPublished(null);
    // A stage half drawn is held back: it is certain to be refused, and for a reason this form can
    // say. *No* stages is deliberately not held back - see the file header. **Pressing the button
    // says which box** (Q-06, G-P8-45): it was greyed with four possible reasons and named none.
    const ok = checks.run([
      {
        field: "key",
        ok: keyOk,
        message:
          key.trim() === ""
            ? "Give the route a key, such as manual-figures."
            : "Use lower case letters, digits, dots, dashes and underscores, starting with a letter or digit.",
      },
      { field: "name", ok: name.trim() !== "", message: "Give the route a name." },
      ...rows.flatMap((r, index) => [
        { field: `stage-name-${String(r.id)}`, ok: r.name.trim() !== "", message: `Name stage ${String(index + 1)}.` },
        {
          field: `stage-reviewer-${String(r.id)}`,
          ok: r.reviewerId !== "",
          message: `Choose a reviewer for stage ${String(index + 1)}.`,
        },
      ]),
    ]);
    if (!ok || publish.isPending) return;
    try {
      const result = await publish.mutateAsync({
        key,
        name,
        appliesTo,
        requiresSeparation: separation,
        stages: rows.map((r) => ({ name: r.name, reviewerId: r.reviewerId })),
      });
      setPublished(result);
      // Cleared only on success. A refusal leaves everything where it was, so the Admin corrects
      // the one thing it names instead of retyping the route.
      setKey("");
      setName("");
      setRows([]);
    } catch {
      // The mutation holds the error and the form renders it: nothing to do here but not let a
      // refusal become an unhandled rejection.
    }
  }

  function addStage() {
    setRows((current) => [...current, { id: nextId, name: "", reviewerId: "" }]);
    setNextId((n) => n + 1);
  }

  function patchStage(id: number, patch: Partial<Row>) {
    setRows((current) => current.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    publish.reset();
  }

  const refusalCode = publish.error instanceof Refused ? publish.error.code : "";

  return (
    <Panel
      title="Approval routes"
      sub="published here, never edited: a change is the next version"
      column="1 / 9"
    >
      <Loaded query={members} of="The people who can review">
        {(everyone) => {
          const reviewers = eligibleReviewers(everyone);
          return (
            <form
              ref={attach}
              className={cls(own.form)}
              onSubmit={(event) => void submit(event)}
              noValidate
              data-testid="route-form"
            >
              <span className={cls(own.fieldRow)}>
                <label className={cls(own.field)}>
                  Key
                  <input
                    type="text"
                    value={key}
                    autoComplete="off"
                    {...checks.field("key")}
                    aria-invalid={(key !== "" && !keyOk) || checks.errors.key !== undefined}
                    data-testid="route-key"
                    onChange={(event) => {
                      checks.touch("key");
                      setKey(event.target.value);
                      publish.reset();
                    }}
                  />
                  <FieldError {...checks.say("key")} />
                  <span className={cls(own.hint)}>
                    Lower case letters, digits, dots, dashes and underscores. An existing key
                    publishes its next version.
                  </span>
                </label>
                <label className={cls(own.field)}>
                  Name
                  <input
                    type="text"
                    value={name}
                    autoComplete="off"
                    {...checks.field("name")}
                    data-testid="route-name"
                    onChange={(event) => {
                      checks.touch("name");
                      setName(event.target.value);
                      publish.reset();
                    }}
                  />
                  <FieldError {...checks.say("name")} />
                </label>
                <label className={cls(own.field)}>
                  Reviews
                  <select
                    value={appliesTo}
                    data-testid="route-applies-to"
                    onChange={(event) => {
                      setAppliesTo(event.target.value as RouteKind);
                      publish.reset();
                    }}
                  >
                    {ROUTE_KINDS.map((kind) => (
                      <option key={kind} value={kind}>
                        {kindLabel(kind)}
                      </option>
                    ))}
                  </select>
                </label>
              </span>

              <label className={cls(own.check)}>
                <input
                  type="checkbox"
                  checked={separation}
                  data-testid="route-separation"
                  onChange={(event) => setSeparation(event.target.checked)}
                />
                The person who submitted a record cannot review it
              </label>

              <fieldset className={cls(own.stagesBox)} data-testid="route-stages">
                <legend className={cls(own.hint)}>
                  Stages run in the order listed. The first listed is stage 1.
                </legend>
                <ol className={cls(own.stages)}>
                  {rows.map((row, index) => (
                    <li key={row.id} className={cls(own.stage)} data-testid="route-stage">
                      <label className={cls(own.field)}>
                        Stage name
                        <input
                          type="text"
                          value={row.name}
                          autoComplete="off"
                          {...checks.field(`stage-name-${String(row.id)}`)}
                          data-testid="route-stage-name"
                          onChange={(event) => {
                            checks.touch(`stage-name-${String(row.id)}`);
                            patchStage(row.id, { name: event.target.value });
                          }}
                        />
                        <FieldError {...checks.say(`stage-name-${String(row.id)}`)} />
                      </label>
                      <label className={cls(own.field)}>
                        Reviewer
                        <select
                          value={row.reviewerId}
                          {...checks.field(`stage-reviewer-${String(row.id)}`)}
                          data-testid="route-stage-reviewer"
                          onChange={(event) => {
                            checks.touch(`stage-reviewer-${String(row.id)}`);
                            patchStage(row.id, { reviewerId: event.target.value });
                          }}
                        >
                          <option value="">Choose a reviewer</option>
                          {reviewers.map((m: Member) => (
                            <option key={m.user_id} value={m.user_id}>
                              {m.display_name} ({ROLE_WORD[m.role] ?? m.role})
                            </option>
                          ))}
                        </select>
                        <FieldError {...checks.say(`stage-reviewer-${String(row.id)}`)} />
                      </label>
                      <button
                        type="button"
                        className={styles.secondary}
                        data-testid="route-stage-remove"
                        aria-label={`Remove stage ${index + 1}`}
                        onClick={() => {
                          setRows((current) => current.filter((r) => r.id !== row.id));
                          publish.reset();
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ol>
                <span className={cls(own.hint)}>
                  {reviewers.length === 0
                    ? "Nobody in this workspace is an active Admin or Approver, so there is no one a stage could name."
                    : "Only an active Admin or Approver can review a stage."}
                </span>
                <span>
                  <button
                    type="button"
                    className={styles.secondary}
                    data-testid="route-add-stage"
                    onClick={addStage}
                  >
                    Add a stage
                  </button>
                </span>
              </fieldset>

              {publish.error ? (
                <div data-testid="route-refusal" data-code={refusalCode}>
                  <WriteFailed error={publish.error} of="that route" testId="route-refused" />
                </div>
              ) : null}

              {published ? (
                <p
                  className={cls(own.published)}
                  role="status"
                  data-testid="route-published"
                  data-key={published.key}
                  data-version={published.version}
                  data-already={published.already_published ? "true" : "false"}
                >
                  {published.already_published ? (
                    <>
                      <strong>{published.name}</strong> is already published exactly like this, as
                      version {published.version}. Nothing was recorded.
                    </>
                  ) : (
                    <>
                      Published <strong>{published.name}</strong> as version {published.version}{" "}
                      with {published.stages.length}{" "}
                      {published.stages.length === 1 ? "stage" : "stages"}.
                    </>
                  )}
                </p>
              ) : null}

              <span>
                <button
                  type="submit"
                  className={styles.primary}
                  disabled={publish.isPending}
                  data-testid="route-submit"
                >
                  {publish.isPending ? "Publishing…" : "Publish route"}
                </button>
              </span>
            </form>
          );
        }}
      </Loaded>

      <h3 className={cls(own.listHead)}>Published routes</h3>
      <Loaded
        query={routes}
        of="The approval routes"
        empty="No approval route is published. Until one is, nothing can be submitted for approval."
      >
        {(rows) => <RouteList rows={rows} members={members.data} />}
      </Loaded>
    </Panel>
  );
}

function RouteList({ rows, members }: { rows: RouteRow[]; members: Member[] | undefined }) {
  const names = new Map((members ?? []).map((m) => [m.user_id, m.display_name]));
  // **Older versions stay listed and say they are older** (journey walk, 2026-10-31). Republishing a
  // route adds a version and removes none, so the list showed "standard_review - version 2" above
  // "standard_review - version 1" with nothing saying which one a submission would use.
  const newest = new Map<string, number>();
  for (const row of rows) newest.set(row.key, Math.max(newest.get(row.key) ?? 0, row.version));
  return (
    <ul className={cls(own.routes)} data-testid="route-list">
      {rows.map((route) => (
        <li
          key={route.id}
          className={cls(own.route)}
          data-testid="route-row"
          data-key={route.key}
          data-version={route.version}
        >
          <span className={cls(own.routeHead)}>
            {route.name}
            <span className={cls(own.routeMeta)}>
              {route.key} · version {route.version} · reviews {kindLabel(route.applies_to)}
              {route.requires_separation ? " · submitter cannot review" : ""}
              {route.version < (newest.get(route.key) ?? route.version) ? (
                <span data-testid="route-superseded">
                  {" "}
                  · replaced by version {newest.get(route.key)}; requests already submitted keep this one
                </span>
              ) : null}
            </span>
          </span>
          <ol className={cls(own.routeStages)}>
            {route.stages.map((stage) => (
              <li key={stage.position} data-testid="route-row-stage">
                {stage.name} — {names.get(stage.reviewer_id) ?? "someone no longer listed"}
              </li>
            ))}
          </ol>
        </li>
      ))}
    </ul>
  );
}
